import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Skeleton, StatCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, CheckCircle2, XCircle, MinusCircle, FileDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const improvements = (r) => {
  const out = [];
  if (r.image_count < 7) out.push(`Add ${7 - r.image_count} more image${7 - r.image_count > 1 ? "s" : ""} (currently ${r.image_count}, ideal 7+): lifestyle shots, size charts, infographics`);
  if (r.bullet_count < 5) out.push(`Add ${5 - r.bullet_count} more bullet point${5 - r.bullet_count > 1 ? "s" : ""} (currently ${r.bullet_count}, ideal 5) highlighting benefits & specs`);
  if (r.title_length < 80) out.push(`Lengthen title to 80-200 chars (currently ${r.title_length}) - include brand, product type, key features, size/colour`);
  else if (r.title_length > 200) out.push(`Shorten title to under 200 chars (currently ${r.title_length}) - Amazon may truncate it in search`);
  if (r.has_aplus === false) out.push("Add A+ Content (Seller Central > Advertising > A+ Content Manager) - typically lifts conversion 5-10%");
  if (!r.has_description && r.has_aplus !== true) out.push("Add a product description as fallback for surfaces where A+ doesn't render");
  if (out.length === 0) out.push("Listing content is in great shape - consider adding video via Seller Central (not visible to API) and monitoring reviews");
  return out;
};

const SORTS = {
  sales_desc: { label: "Top sellers first", fn: (a, b) => (b.units_30d || 0) - (a.units_30d || 0) || a.score - b.score },
  score_asc: { label: "Worst score first", fn: (a, b) => a.score - b.score },
  score_desc: { label: "Best score first", fn: (a, b) => b.score - a.score },
};

const GRADE_STYLE = {
  A: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400",
  B: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400",
  C: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400",
  D: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400",
};

const YesNo = ({ v }) =>
  v === true ? <CheckCircle2 className="h-4 w-4 text-green-600" aria-hidden="true" />
    : v === false ? <XCircle className="h-4 w-4 text-red-500" aria-hidden="true" />
      : <MinusCircle className="h-4 w-4 text-muted-foreground" aria-hidden="true" title="Unknown" />;

export const ListingQuality = () => {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("sales_desc");
  const [selected, setSelected] = useState([]);

  const toggle = (sku) => setSelected((s) => s.includes(sku) ? s.filter((x) => x !== sku) : [...s, sku]);

  const exportPdf = async () => {
    const picked = (data.listings || []).filter((r) => selected.includes(r.sku));
    if (!picked.length) return;
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF();
    const pageH = doc.internal.pageSize.getHeight();
    const pageW = doc.internal.pageSize.getWidth();
    let y = 18;
    doc.setFontSize(16);
    doc.setTextColor(20);
    doc.text("Listing Quality — Improvement Report", 14, y);
    y += 6;
    doc.setFontSize(9);
    doc.setTextColor(110);
    doc.text(`Generated ${new Date().toLocaleString("en-IN")} · ${picked.length} listing(s) selected`, 14, y);
    y += 8;
    picked.sort(SORTS[sort].fn).forEach((r, idx) => {
      const fixes = improvements(r);
      const blockH = 26 + fixes.length * 9;
      if (y + blockH > pageH - 12) { doc.addPage(); y = 16; }
      doc.setDrawColor(220);
      doc.line(14, y, pageW - 14, y);
      y += 6;
      doc.setFontSize(11);
      doc.setTextColor(20);
      doc.setFont(undefined, "bold");
      doc.text(doc.splitTextToSize(`${idx + 1}. ${r.name || r.sku}`, pageW - 50), 14, y);
      const g = { A: [22, 163, 74], B: [37, 99, 235], C: [217, 119, 6], D: [220, 38, 38] }[r.grade];
      doc.setTextColor(...g);
      doc.text(`${r.score}/100 (${r.grade})`, pageW - 14, y, { align: "right" });
      doc.setFont(undefined, "normal");
      y += (r.name || "").length > 90 ? 10 : 5;
      doc.setFontSize(8.5);
      doc.setTextColor(110);
      doc.text(`SKU: ${r.sku}  ·  ASIN: ${r.asin}  ·  Units 30d: ${r.units_30d || 0}  ·  Images: ${r.image_count}  ·  Bullets: ${r.bullet_count}  ·  Title: ${r.title_length} chars  ·  A+: ${r.has_aplus === true ? "Yes" : r.has_aplus === false ? "No" : "Unknown"}`, 14, y);
      y += 6;
      doc.setFontSize(9.5);
      doc.setTextColor(40);
      fixes.forEach((f) => {
        const lines = doc.splitTextToSize(`•  ${f}`, pageW - 34);
        if (y + lines.length * 4.5 > pageH - 12) { doc.addPage(); y = 16; }
        doc.text(lines, 18, y);
        y += lines.length * 4.5 + 1.5;
      });
      y += 4;
    });
    doc.save(`listing-improvements-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success(`PDF downloaded — ${picked.length} listing(s) with improvement checklists`);
  };

  const isActive = (state) => {
    if (!state?.running) return false;
    const hb = state.heartbeat || state.started_at;
    if (!hb) return true;
    return Date.now() - new Date(hb).getTime() < 10 * 60 * 1000;
  };

  const load = () => api.get("/tools/listing-quality").then(({ data }) => {
    setData(data);
    setSyncing(isActive(data.state));
  });
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!syncing) return;
    const t = setInterval(async () => {
      const { data: d } = await api.get("/tools/listing-quality");
      if (!isActive(d.state)) {
        clearInterval(t);
        setSyncing(false);
        if (d.state?.error) toast.error(`Listing scan failed: ${d.state.error}`);
        else toast.success(`Listing quality updated — ${d.state?.rows || 0} listings scored`);
        setData(d);
      } else {
        setData(d);
      }
    }, 10000);
    return () => clearInterval(t);
  }, [syncing]);

  const sync = async () => {
    await api.post("/tools/listing-quality/sync");
    toast.info("Scanning your listings via the Amazon Catalog API — this takes a few minutes…");
    setSyncing(true);
  };

  if (!data) return <Skeleton className="h-64" />;
  const rows = data.listings || [];
  const visible = rows.filter((r) => ((r.name || "") + (r.sku || "") + (r.asin || "")).toLowerCase().includes(filter.toLowerCase())).sort(SORTS[sort].fn).slice(0, 500);
  const allVisibleSelected = visible.length > 0 && visible.every((r) => selected.includes(r.sku));
  const toggleAll = () => setSelected(allVisibleSelected
    ? selected.filter((s) => !visible.some((r) => r.sku === s))
    : [...new Set([...selected, ...visible.map((r) => r.sku)])]);

  return (
    <div className="space-y-6" data-testid="listing-quality">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Content quality of each Amazon listing — images, bullet points, title length, description and A+ content —
          scored out of 100. <strong>Videos are not exposed by the SP-API</strong>, so they're excluded from the score.
        </p>
        <Button size="sm" className="rounded-full" onClick={sync} disabled={syncing} data-testid="lq-sync-button">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} aria-hidden="true" />
          {syncing
            ? (data.state?.total ? `Scanning ${data.state?.scanned || 0}/${data.state.total}…` : "Scanning listings…")
            : rows.length ? "Rescan listings" : "Scan listings"}
        </Button>
      </div>

      {rows.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Average score" value={`${data.avg_score}/100`} sub={`${rows.length} listings`} testId="lq-avg-kpi" />
          {"ABCD".split("").map((g) => (
            <StatCard key={g} label={`Grade ${g}`} value={data.grades?.[g] ?? 0}
              sub={g === "A" ? "85+" : g === "B" ? "70–84" : g === "C" ? "50–69" : "below 50"} testId={`lq-grade-${g}-kpi`} />
          ))}
        </div>
      )}

      {rows.length === 0 ? (
        <p className="p-6 text-sm text-muted-foreground bg-card border border-border rounded-lg">
          No listing scan yet — click "Scan listings" to fetch content quality from Amazon (takes a few minutes for all ASINs).
        </p>
      ) : (
        <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden" data-testid="lq-table-card">
          <div className="p-3 border-b border-border flex items-center gap-3 flex-wrap">
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search by product, SKU or ASIN…"
              data-testid="lq-search-input"
              className="w-full max-w-sm h-9 rounded-full border border-border bg-background px-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              Sort
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-44 h-9 rounded-full text-xs" data-testid="lq-sort"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(SORTS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`lq-sort-${k}`}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-sm" data-testid="lq-table">
            <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} data-testid="lq-select-all" title="Select all visible" />
                </th>
                {["Product", "SKU", "ASIN", "Units (30d)", "Images", "Bullets", "Title chars", "Description", "A+ Content", "Score", "Grade"].map((h) =>
                <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.sku || r.asin} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`lq-row-${r.sku || r.asin}`}>
                  <td className="px-4 py-2.5">
                    <input type="checkbox" checked={selected.includes(r.sku)} onChange={() => toggle(r.sku)} data-testid={`lq-check-${r.sku}`} />
                  </td>
                  <td className="px-4 py-2.5 font-medium max-w-xs truncate">{r.name || r.sku || r.asin}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{r.sku}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
                    <a href={`https://www.amazon.in/dp/${r.asin}`} target="_blank" rel="noreferrer" className="hover:underline">{r.asin}</a>
                  </td>
                  <td className="px-4 py-2.5 font-semibold">{(r.units_30d || 0).toLocaleString("en-IN")}</td>
                  <td className={`px-4 py-2.5 ${r.image_count < 5 ? "text-red-600 font-semibold" : ""}`}>{r.image_count}</td>
                  <td className={`px-4 py-2.5 ${r.bullet_count < 5 ? "text-red-600 font-semibold" : ""}`}>{r.bullet_count}</td>
                  <td className="px-4 py-2.5">{r.title_length}</td>
                  <td className="px-4 py-2.5"><YesNo v={r.has_description} /></td>
                  <td className="px-4 py-2.5"><YesNo v={r.has_aplus} /></td>
                  <td className="px-4 py-2.5 font-heading font-bold">{r.score}</td>
                  <td className="px-4 py-2.5"><Badge className={`${GRADE_STYLE[r.grade]} border-0 font-bold`}>{r.grade}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Scoring: images (30 pts, 7+ ideal) · bullet points (25 pts, 5 ideal) · title length (20 pts, 80–200 chars ideal) · A+ content (25 pts) or plain description (15 pts).
      </p>
    </div>
  );
};
